/**
 * Typed API client.
 *
 * Wraps `fetch` with:
 *   - Base URL from env (VITE_API_BASE_URL; defaults to /api in dev, proxied
 *     by vite.config.ts to localhost:3000)
 *   - Automatic JWT Bearer injection from the token store
 *   - Automatic 401 → token refresh attempt (single retry)
 *   - Typed error objects that callers can discriminate on
 */

import type { ApiError } from '@/types/api';
import { tokenStore } from '@/store/auth';

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiClientError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
  retrying = false,
): Promise<T> {
  const token = tokenStore.getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 401 && !retrying) {
    /*
      Retry with the SAME extra headers. An `Idempotency-Key` dropped on the
      retry would make the replay look like a fresh request to the server — on
      `savings/unlock` that is a second, different FIFO tranche released.
    */
    const refreshed = await tokenStore.refresh();
    if (refreshed) return request<T>(method, path, body, extraHeaders, true);
  }

  if (!resp.ok) {
    let error: ApiError;
    try {
      const payload = (await resp.json()) as { error?: ApiError };
      error = payload.error ?? { code: 'unknown', message: resp.statusText };
    } catch {
      error = { code: 'parse_error', message: resp.statusText };
    }
    throw new ApiClientError(resp.status, error);
  }

  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, body, headers),
  put: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('PUT', path, body, headers),
  /*
    DELETE carries a body on exactly one route: clearing a fixture quarantine,
    where the contract REQUIRES a reason. That reason is the audit record for
    an operator decision to let a withheld fixture back toward the board, so
    the verb being DELETE does not make the payload optional.
  */
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
